<script setup lang="ts">
import { api } from '@/api';
import {
  EvaluationParameterDto,
  ImportTrackItMeasurementForWorkStepDto,
  ImportTrackItMeasurementForWorkStepDtoTypeEnum,
  TrackItTaskResultForEvaluation,
  TrackItTaskWorkItemDto,
  TrackItTaskWorkResultDto,
  TrackItTaskWorkStepDto,
} from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import TaskStepTitle from '@/components/TaskStepTitle/TaskStepTitle.vue';
import { useTaskStepTitle } from '@/components/TaskStepTitle/useTaskStepTitles';
import { truthy } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import { useToggle } from '@vueuse/core';
import { mdiSwapHorizontal } from '@mdi/js';
import { computed, onMounted, ref } from 'vue';
import { useJobOccurrence } from '../../utils/useJobOccurrence';
import {
  buildTrackItTaskResultForEvaluation,
  TrackItManualValue,
} from '../../utils/trackItTaskResultForEvaluation';
import { useTrackItWorkStepPreview } from '../../utils/useTrackItWorkStepPreview';
import TrackItMeasurementPickerDialog from './TrackItMeasurementPickerDialog.vue';
import TrackItMeasurementResultPreview from './TrackItMeasurementResultPreview.vue';

const props = defineProps<{
  workitem: TrackItTaskWorkItemDto;
}>();

const modelValue = defineModel<boolean>({ required: true });

const { jobOccurrence, reload } = useJobOccurrence();
const { fetchPreview: fetchWorkStepPreview } = useTrackItWorkStepPreview();

const loading = ref(true);
const applying = ref(false);
const manualValueLoading = ref(false);
const results = ref<TrackItTaskWorkResultDto[]>([]);
const additionalResultsByStep = ref<Record<string, TrackItTaskResultForEvaluation[]>>({});
const manuallyAssignedStepIds = ref<Set<string>>(new Set());

const allCombinations = computed(() =>
  props.workitem.workSteps?.map((step) => step.combination).filter(truthy),
);

const { getTitle } = useTaskStepTitle(allCombinations);

function resultForStep(workStepId: string) {
  return results.value.find((result) => result.workStepId === workStepId);
}

// Every measurement picked in this dialog, so the picker doesn't offer one twice.
const pickedMeasurementIds = computed(() =>
  results.value
    .map((result) => result.trackItMeasurementId)
    .filter((id): id is number => id !== undefined),
);

function buildImportMeasurementsForWorkSteps(): ImportTrackItMeasurementForWorkStepDto[] {
  if (!jobOccurrence.value.id) {
    return [];
  }

  const jobOccurrenceId = jobOccurrence.value.id;

  return (props.workitem.workSteps ?? [])
    .map((step) => {
      const result = resultForStep(step.id);

      if (!result || result.trackItMeasurementId === undefined) {
        return undefined;
      }

      return {
        resultId: result.id,
        trackItMeasurementId: result.trackItMeasurementId,
        jobOccurrenceId,
        workStepId: step.id,
        additionalResults: additionalResultsByStep.value[step.id] ?? [],
        $type:
          ImportTrackItMeasurementForWorkStepDtoTypeEnum.PtwPtw100AppZeroApplicationSharedTrackItImportComponentDtoImportTrackItMeasurementForWorkStepDtoPtwPtw100AppZeroApplicationShared,
      } satisfies ImportTrackItMeasurementForWorkStepDto;
    })
    .filter(truthy);
}

onMounted(async () => {
  if (!jobOccurrence.value.id) {
    loading.value = false;
    return;
  }

  const { result } = await api.trackItImportApi.getTrackItTaskExecutionPreviewForWorkItem({
    previewImportTrackItMeasurementsForWorkItemDto: {
      jobOccurrenceId: jobOccurrence.value.id,
      workItemId: props.workitem.id,
    },
  });

  results.value = result ?? [];
  loading.value = false;
});

const [pickerOpen, togglePicker] = useToggle(false);

const pickerStep = ref<TrackItTaskWorkStepDto | undefined>(undefined);

function openPicker(workStep: TrackItTaskWorkStepDto) {
  pickerStep.value = workStep;
  togglePicker(true);
}

// Deletes the step's picked-but-not-yet-applied measurement outright, freeing it up for another
// workstep in this same dialog to use. Nothing is persisted yet at this point — Apply is what
// actually imports — so this is just local state cleanup, no backend call needed.
//
// "Change" (re-opening the picker on a step that already has one) deliberately does NOT call
// this first: pickedMeasurementIds tells the picker's own "available measurements" fetch
// which measurements are already used elsewhere in this dialog, precisely so it can exclude
// them — clearing this step's own entry first would make its own current pick look available
// again and let the user "pick" the exact same one right back.
function clearStepSelection(stepId: string) {
  results.value = results.value.filter((result) => result.workStepId !== stepId);
  const remainingAdditionalResults = { ...additionalResultsByStep.value };
  delete remainingAdditionalResults[stepId];
  additionalResultsByStep.value = remainingAdditionalResults;
  manuallyAssignedStepIds.value = new Set(
    [...manuallyAssignedStepIds.value].filter((id) => id !== stepId),
  );
}

function onPickerApply(payload: {
  trackItMeasurementId: number;
  additionalResults: TrackItTaskResultForEvaluation[];
  result: TrackItTaskWorkResultDto;
}) {
  if (!pickerStep.value) {
    return;
  }

  const workStepId = pickerStep.value.id;

  results.value = [
    ...results.value.filter((result) => result.workStepId !== workStepId),
    payload.result,
  ];

  additionalResultsByStep.value = {
    ...additionalResultsByStep.value,
    [workStepId]: payload.additionalResults,
  };

  manuallyAssignedStepIds.value = new Set(manuallyAssignedStepIds.value).add(workStepId);
}

async function onManualValue(
  step: TrackItTaskWorkStepDto,
  evaluation: EvaluationParameterDto,
  value: TrackItManualValue | null,
) {
  const result = resultForStep(step.id);

  if (!result || result.trackItMeasurementId === undefined) {
    return;
  }

  const withoutEvaluation = (additionalResultsByStep.value[step.id] ?? []).filter(
    (additionalResult) => additionalResult.evaluationParameterId !== evaluation.id,
  );
  // An emptied field means "no manual value for this evaluation" — nothing to persist yet in
  // this pre-import preview, so it's dropped rather than kept as an explicit null entry.
  const updatedAdditionalResults =
    value === null
      ? withoutEvaluation
      : [...withoutEvaluation, buildTrackItTaskResultForEvaluation(evaluation, value)];

  manualValueLoading.value = true;
  const updatedResult = await fetchWorkStepPreview(
    result.trackItMeasurementId,
    step.id,
    updatedAdditionalResults,
  );
  manualValueLoading.value = false;

  if (updatedResult) {
    results.value = [...results.value.filter((r) => r.workStepId !== step.id), updatedResult];
    additionalResultsByStep.value = {
      ...additionalResultsByStep.value,
      [step.id]: updatedAdditionalResults,
    };
  }
}

function close() {
  modelValue.value = false;
}

async function apply() {
  if (!jobOccurrence.value.id) {
    return;
  }

  applying.value = true;

  const { success } = await api.trackItImportApi.importTrackItMeasurementsForWorkItem({
    importTrackItMeasurementsForWorkItemDto: {
      jobOccurrenceId: jobOccurrence.value.id,
      workItemId: props.workitem.id,
      importMeasurementsForWorkSteps: buildImportMeasurementsForWorkSteps(),
    },
  });

  applying.value = false;

  if (success) {
    await reload();
    close();
  }
}
</script>

<template>
  <TrackItMeasurementPickerDialog
    v-if="pickerOpen && pickerStep"
    v-model="pickerOpen"
    :workStep="pickerStep"
    :skipPreview="true"
    :measurementIdsToExclude="pickedMeasurementIds"
    @apply="onPickerApply"
  />

  <Dialog
    :modelValue="modelValue"
    size="x-large"
    @update:modelValue="(value) => (modelValue = value)"
  >
    <template #title>
      <h3>{{ $t('Preview') }}</h3>
    </template>

    <template #text>
      <p v-if="loading">Loading measurements...</p>

      <template v-else>
        <div v-for="step in workitem.workSteps" :key="step.id" class="mb-6 pb-6 border-b-thin">
          <div class="d-flex align-center justify-space-between mb-2">
            <TaskStepTitle :combination="getTitle(step.combination)" />

            <Button
              v-if="!resultForStep(step.id)"
              variant="secondary"
              size="small"
              :label="$t('Import')"
              @click="openPicker(step)"
            />

            <!-- A result the user never actually chose — auto-suggested on load and never
            touched via the picker — is not theirs to change or delete here. -->
            <div v-else-if="manuallyAssignedStepIds.has(step.id)" class="d-flex align-center">
              <Button
                variant="text"
                size="small"
                :icon="mdiSwapHorizontal"
                :aria-label="$t('Repeat')"
                @click="openPicker(step)"
              />
              <PtwDeleteButton size="small" @delete="clearStepSelection(step.id)" />
            </div>
          </div>

          <TrackItMeasurementResultPreview
            v-if="resultForStep(step.id)"
            :result="resultForStep(step.id)!"
            :evaluations="step.evaluations ?? undefined"
            :manualEntryEnabled="manuallyAssignedStepIds.has(step.id)"
            :loading="manualValueLoading"
            @manualValue="(evaluation, value) => onManualValue(step, evaluation, value)"
          />

          <p v-else>{{ $t('TrackItNoMatchingMeasurements' as I18nKey) }}</p>
        </div>
      </template>
    </template>

    <template #actions>
      <TextVBtn :text="$t('Cancel')" @click="close" />
      <SecondaryVBtn :text="$t('Apply')" :loading="applying" @click="apply" />
    </template>
  </Dialog>
</template>
